import { useQuery } from '@apollo/client';
import { KeyboardArrowRight } from '@mui/icons-material';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { FolderContextProvider, findLandingFolderId, useAppContext } from '~/contexts';
import { useAccessibleFolders, type FolderTreeResult } from '~/hooks/folders';
import { assert } from '~/lib/assert';
import { FolderBreadcrumbs } from '../folder-breadcrumbs';
import { FolderIcon } from '../folder-breadcrumbs/styles';
import { SelectContentFolderDocument } from './SelectContentFolder.generated';
import { Dialog, DialogContentTitle, DialogTitle } from './components';
import { DialogActions, DialogContent } from './lib/styles';

const FolderListItem = ({
  folder,
  onClick,
}: {
  folder: FolderTreeResult;
  onClick: (folder: FolderTreeResult) => void;
}) => {
  const { t } = useTranslation();
  const folderName = folder.name === 'No Folder' ? t('common.fallbacks.root_folder') : folder.name;
  return (
    <ListItem
      disableGutters
      disablePadding
      key={folder.id}
      secondaryAction={
        folder.children.length > 0 && !folder.isRoot ? (
          <IconButton
            label={t('common.actions.open_folder', { name: folderName })}
            onClick={(event) => {
              event.stopPropagation();
              onClick(folder);
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
        ) : null
      }
    >
      <Tooltip
        title={
          !folder.canUpdate.value ? 'You do not have permission to add content to this folder' : ''
        }
      >
        <span>
          <Radio
            disabled={!folder.canUpdate.value}
            inputProps={{ 'aria-label': folderName }}
            sx={{
              '&.Mui-disabled': {
                boxShadow: 'none',
                color: 'rgba(0,0,0,.2)',
              },
            }}
            value={folder.id}
          />
        </span>
      </Tooltip>
      <ListItemButton
        onClick={(event) => {
          event.stopPropagation();
          onClick(folder);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ListItemIcon sx={{ display: 'flex', alignItems: 'center' }}>
          <FolderIcon />
        </ListItemIcon>
        <ListItemText primary={folderName} />
      </ListItemButton>
    </ListItem>
  );
};

export interface SelectFolderDialogProps {
  description?: string;
  onSubmit?: (folderId: string) => void;
  onClose?: () => void;
  open: boolean;
}

export const SelectFolderDialog = ({
  description,
  onClose,
  onSubmit,
  open,
}: SelectFolderDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  // State to track the user clicking on folders to get to children; undefined until they navigate,
  // null for the top level
  const [navigatedFolder, setNavigatedFolder] = useState<FolderTreeResult | null>();

  // State for the folder the user has selected
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  const { data } = useQuery(SelectContentFolderDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const { accessibleFolders, findFolder, findParent } = useAccessibleFolders(
    data?.network?.contentFolderTree,
    data?.network?.contentFolders,
  );

  // Open on the landing folder's parent so the landing folder itself can be selected
  const landingFolderId = data?.network
    ? findLandingFolderId(data.network.contentFolderTree, data.network.contentFolders)
    : undefined;
  const currentFolder =
    navigatedFolder === undefined ? findParent(landingFolderId) : navigatedFolder;

  // Preselect the landing folder, but never default a move into the root
  const landingFolder = landingFolderId == null ? undefined : findFolder(landingFolderId);
  const folderId =
    selectedFolder ||
    (landingFolder?.canUpdate.value && !landingFolder.isRoot ? String(landingFolder.id) : '');

  const updateCurrentFolder = useCallback(
    (folderId: number) => {
      // Get the folder from the tree
      const folder = findFolder(folderId);

      // Need to handle the scenario where they click on the root in the breadcrumb but don't have access to it.
      if (!folder || folder.isRoot) {
        setNavigatedFolder(null);
        return;
      }

      setNavigatedFolder(folder);
    },
    [findFolder],
  );

  const handleClose = useCallback(() => {
    setNavigatedFolder(undefined);
    setSelectedFolder('');
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    onSubmit?.(folderId);
    handleClose();
  }, [folderId, handleClose, onSubmit]);

  const handleClick = useCallback(
    (folderId: number) => {
      updateCurrentFolder(folderId);
    },
    [updateCurrentFolder],
  );

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSelectedFolder(event.target.value);
  };

  const handleFolderClick = useCallback(
    (folder: FolderTreeResult) => {
      if (folder.children.length === 0 || folder.isRoot) {
        setSelectedFolder(String(folder.id));
      } else {
        handleClick(folder.id);
      }
    },
    [handleClick],
  );

  return (
    <>
      {data?.network?.contentFolderTree && (
        <FolderContextProvider folderTree={data.network.contentFolderTree}>
          <Dialog fullWidth maxWidth="xs" onClose={handleClose} open={open}>
            <DialogTitle onClose={handleClose}>
              {t('components.select_content_folder.title')}
            </DialogTitle>

            <DialogContent>
              <DialogContentTitle id="select-content-folder-description">
                {description}
              </DialogContentTitle>

              <Box
                sx={{
                  width: '100%',
                  mb: 1,
                }}
              >
                {currentFolder ? (
                  <FolderBreadcrumbs
                    allItems={() => {
                      assert(data.network?.contentFolderTree != null, 'tree is null');
                      updateCurrentFolder(data.network.contentFolderTree.id);
                    }}
                    label={t('components.select_content_folder.all_folders')}
                    changeFolder={(id) => updateCurrentFolder(id)}
                    folder={currentFolder}
                  />
                ) : (
                  <div>{t('components.select_content_folder.all_folders')}</div>
                )}
              </Box>

              <Box
                sx={{
                  minHeight: '200px',
                }}
              >
                <RadioGroup
                  aria-labelledby="select-content-folder-description"
                  value={folderId}
                  onChange={handleToggle}
                >
                  <List>
                    {(currentFolder?.children || accessibleFolders).map((x) => (
                      <FolderListItem key={x.id} folder={x} onClick={handleFolderClick} />
                    ))}
                  </List>
                </RadioGroup>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={!folderId}
                type="submit"
                variant="contained"
                onClick={handleSubmit}
              >
                Select
              </Button>
            </DialogActions>
          </Dialog>
        </FolderContextProvider>
      )}
    </>
  );
};
